import { Box, Grid } from '@mui/material';
import { Text, fontWeight, textLevel } from '../../../../../packages/ui/atoms/typography';
import { colors } from '../../../../../packages/colors';
import { Avatar, avatarType } from '../../../../../packages/ui/atoms/avatar';
import { Badge, badgeSize, badgeStyle, badgeType } from '../../../../../packages/ui/atoms/badge';
import { Icons } from '../../../../../packages/icons';

import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';

import { getAuth } from '../../../../../redux/modules/auth/selectors';
import { getProfile } from '../../../../../redux/modules/profile/selectors';
import { profileActionCreators } from '../../../../../redux/modules/profile';
import { createNewAssociatedTag } from '../../../../../services/member/member.service';

import { useDomainType } from '../../../../../hooks/useDomainType';
import { DomainTypesEnum } from '../../../../../constants/CommonConstants';
import history from '../../../../../utils/history';
import Card from './Card';
import MedicationDetail from '../Medications/medication-detail';
import AddNewReport from '../Medications/medication-new';
import AddNewReportMedications from '../../../../provider-v2/member-detail/add-new-report/AddNewReportMedications';

import { useStyles } from './index.styles';

const Medications = ({ isAdmin }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { memberId } = useParams();
  const { meta } = useSelector(getAuth);
  const { profile, selectedPatient } = useSelector(getProfile);

  const [isLoading, domainData, domainOptions, lookupData] = useDomainType({
    type: DomainTypesEnum.MEDICATIONS,
  });

  const [isSaving, setIsSaving] = useState(false);
  const [errorApi, setError] = useState(null);
  const [selectedMedication, setSelectedMedication] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showAddMedication, setShowAddMedication] = useState(false);

  const onClickMedication = (item, isEdit = false) => {
    setSelectedMedication(item);
    setIsEditing(isEdit);
  };

  const onCloseDrawer = () => {
    setSelectedMedication(null);
    setIsEditing(false);
    setShowAddMedication(false);
  };

  const onClickNewReport = () => {
    setIsEditing(true);
  };
  const onUpdateItem = async payload => {
    setIsSaving(true);
    setError(null);
    try {
      // Handle unified flow payload for editing
      const medicationData = payload.medications[0]; // Since editing only has one medication
      const editPayload = {
        patientId: memberId,
        tagItems: [
          {
            assignedBy: profile.fullName || meta.nickName,
            domainElementId: selectedMedication.domainElementId,
            metaData: {
              notes: medicationData?.tutorial?.additionalNotes,
              selectedImportance: medicationData?.status,
              rxDrugInfo: {
                sig: medicationData?.tutorial?.sig,
                dispenseAmount: medicationData?.tutorial?.dispenseAmount
                  ? Number(medicationData.tutorial.dispenseAmount)
                  : 0,
                associatedDiagnoses: medicationData?.tutorial.associatedDiagnoses,
                allowSubstitutions: medicationData?.tutorial?.allowSubstitutions,
                pharmacyNotes: medicationData?.tutorial?.pharmacyNotes,
                additionalNotes: medicationData?.tutorial?.additionalNotes,
                quantity: medicationData.tutorial.quantity ? Number(medicationData.tutorial.quantity) : 0,
                dose: medicationData.tutorial.dose ?? '',
                frequency: medicationData?.tutorial?.frequency?.value ?? medicationData?.tutorial?.frequency,
                duration: medicationData?.tutorial?.duration ? Number(medicationData.tutorial.duration) : 0,
                confidantProviderId: medicationData?.tutorial?.confidantProviderId,
                confidantProviderName: medicationData?.tutorial?.confidantProviderName,
                prescribingProvider:
                  medicationData?.tutorial?.prescribingProvider === 'Confidant Provider'
                    ? 'Internal'
                    : 'External',
                providerName: medicationData?.tutorial?.providerName,
                symptomsBeingTreated: medicationData?.tutorial?.symptomsBeingTreated,
                externalMedicationId: medicationData?.tutorial?.externalMedicationId,
                externalMedicationName: medicationData?.tutorial?.externalMedicationName,
                refills: medicationData?.tutorial?.refills ? Number(medicationData.tutorial.refills) : 0,
                pharmacyId: payload.pharmacy?.id,
                pharmacyName: payload.pharmacy?.name,
                pharmacyCode: payload.pharmacy?.externalId,
                pharmacyZipCode: payload.pharmacy?.zipCode,
              },
            },
          },
        ],
      };
      await createNewAssociatedTag(editPayload);
      setIsSaving(false);
      dispatch(profileActionCreators.fetchDomainTypes(memberId));
      onCloseDrawer();
    } catch (error) {
      setError(error.data.message || error.data.errors[0]?.endUserMessage);
      setIsSaving(false);
    }
  };

  const onSubmitNewReport = payload => {
    const data = {
      patientId: memberId,
      tagItems: payload.map(item => ({
        assignedBy: profile.fullName || meta.nickName,
        domainElementId: item.item.value,
        metaData: {
          // interferenceInLife: item.isInterference,
          notes: item?.notes,
          selectedImportance: item?.status,
          rxDrugInfo: {
            dose: 0,
            doseUnit: '',
            doseFrequency: 0,
            // refillable: true,
            // refill: item.tutorial.refillTimes.amount,
            supply: 0,
            supplyUnit: '',
            confidantProviderId: item?.tutorial?.confidantProviderId,
            confidantProviderName: item?.tutorial?.confidantProviderName,
            dosage: item?.tutorial?.dosage,
            prescribingProvider:
              item?.tutorial?.prescribingProvider === 'Confidant Provider' ? 'Internal' : 'External',
            providerName: item?.tutorial?.providerName,
            symptomsBeingTreated: item?.tutorial?.symptomsBeingTreated,
            frequency: item?.tutorial?.frequency,
          },
        },
      })),
    };
    dispatch(
      profileActionCreators.createNewAssociatedTag({
        data,
        callback: errorMsg => {
          if (!errorMsg) {
            onCloseDrawer();
          }
        },
      }),
    );
  };

  return (
    <>
      <AddNewReport
        isOpen={showAddMedication}
        onClose={onCloseDrawer}
        options={domainOptions}
        lookupData={lookupData}
        onSubmit={onSubmitNewReport}
      />
      <MedicationDetail
        open={!!selectedMedication && !isEditing}
        onClose={onCloseDrawer}
        onClickNewReport={onClickNewReport}
        medication={selectedMedication}
      />
      <AddNewReportMedications
        isOpen={!!selectedMedication && isEditing}
        onClose={onCloseDrawer}
        patientDiagnoses={[]} // You may need to pass actual patient diagnoses here
        options={domainOptions}
        onSubmit={onUpdateItem}
        lookupData={lookupData}
        memberId={memberId}
        optionsLoading={false}
        isEditMode={true}
        existingMedication={selectedMedication}
        error={errorApi}
        isSaving={isSaving}
      />
      <Card
        label="Medications"
        actions={[
          {
            label: 'View all',
            onClick: () =>
              history.push(`/${isAdmin ? 'admin' : 'provider'}/members/${memberId}?name=medications`),
          },
        ]}
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
            <Icons className="rotate linear infinite" glyph="in-progress" color={colors.primary} />
            Loading...
          </Box>
        ) : domainData?.relatedElements?.length > 0 ? (
          <>
            <Grid container alignItems="center">
              <Grid item xs={2}>
                <Box className={classes.tableHead}>
                  <Text weight={fontWeight.BOLD} level={textLevel.XS} color={colors.neutral700}>
                    Med Name
                  </Text>
                </Box>
              </Grid>
              <Grid item xs={2}>
                <Box className={classes.tableHead}>
                  <Text weight={fontWeight.BOLD} level={textLevel.XS} color={colors.neutral700}>
                    Prescriber
                  </Text>
                </Box>
              </Grid>
              <Grid item xs={1}>
                <Box className={classes.tableHead}>
                  <Text weight={fontWeight.BOLD} level={textLevel.XS} color={colors.neutral700}>
                    Dosage
                  </Text>
                </Box>
              </Grid>
              <Grid item xs={2}>
                <Box className={classes.tableHead}>
                  <Text weight={fontWeight.BOLD} level={textLevel.XS} color={colors.neutral700}>
                    Frequency
                  </Text>
                </Box>
              </Grid>
              <Grid item xs={2}>
                <Box className={classes.tableHead}>
                  <Text weight={fontWeight.BOLD} level={textLevel.XS} color={colors.neutral700}>
                    Date prescribed
                  </Text>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box className={classes.tableHead}>
                  <Text weight={fontWeight.BOLD} level={textLevel.XS} color={colors.neutral700}>
                    Status
                  </Text>
                </Box>
              </Grid>
            </Grid>
            {domainData.relatedElements.slice(0, 3).map((item, idx) => (
              <Grid
                container
                key={idx}
                alignItems="center"
                sx={{ borderBottom: `1px solid ${colors.neutral100}`, cursor: 'pointer' }}
                onClick={() => onClickMedication(item)}
              >
                <Grid item xs={2}>
                  <Box sx={{ p: 2 }}>
                    <Text weight={fontWeight.MEDIUM} level={textLevel.S}>
                      {item?.name || ''}
                    </Text>
                  </Box>
                </Grid>
                <Grid item xs={2}>
                  <Box sx={{ p: 2, display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <Avatar
                      src={item?.avatar}
                      size={40}
                      variant={avatarType.CIRCLE}
                      name={item?.assignedBy}
                    />
                    <Box display="flex" gap="4px" flexDirection="column" justifyContent="flex-start">
                      <Text weight={fontWeight.BOLD} level={textLevel.S}>
                        {item?.assignedBy || ''}
                      </Text>
                      <Text weight={fontWeight.MEDIUM} level={textLevel.S}>
                        {dayjs(item?.assignedAt || '').format('MMMM DD, YYYY')}
                      </Text>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={1}>
                  <Box sx={{ p: 2 }}>
                    <Text weight={fontWeight.MEDIUM} level={textLevel.S}>
                      {item?.tagMetaData?.rxDrugInfo?.dosage}
                    </Text>
                  </Box>
                </Grid>
                <Grid item xs={2}>
                  <Box sx={{ p: 2 }}>
                    <Text weight={fontWeight.MEDIUM} level={textLevel.S}>
                      {item?.tagMetaData?.rxDrugInfo?.frequency}
                    </Text>
                  </Box>
                </Grid>
                <Grid item xs={2}>
                  <Box sx={{ p: 2 }}>
                    <Text weight={fontWeight.MEDIUM} level={textLevel.S}>
                      {dayjs(item?.assignedAt || '').format('MM/DD/YYYY')}
                    </Text>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ p: 2 }}>
                    <Badge
                      size={badgeSize.SMALL}
                      variant={badgeType.OUTLINED}
                      style={badgeStyle[item?.priority.name] || badgeStyle.UNRELATED}
                    >
                      {item?.priority?.name || ''}
                    </Badge>
                  </Box>
                </Grid>
              </Grid>
            ))}
          </>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
            <Text weight={fontWeight.BOLD} level={textLevel.S} color={colors.neutral700}>
              No medications reported{' '}
              {`${selectedPatient?.member?.nickName ? `for ${selectedPatient?.member?.nickName}` : ''}`} yet.
            </Text>
          </Box>
        )}
      </Card>
    </>
  );
};

export default Medications;
